import React, { useEffect, useState } from "react"
import miniCileamoov from "../assets/img/mini_cileamoov.png"
import { useNavigate, useLocation } from "react-router-dom"
import {
    Box,
    Container,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Snackbar,
    IconButton,
    InputAdornment,
} from "@mui/material"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import { useAuth } from "../services/AuthContext"
import { getPrimaryRole } from "../services/navigationConfig"
import { getErrorMessage } from "../models/api"
import { useTranslation } from "react-i18next"

/**
 * Login Page
 *
 * Authentication page with login form
 */
function LoginPage() {
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()
    const { login, loading, error: authError } = useAuth()

    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sessionExpiredToastOpen, setSessionExpiredToastOpen] =
        useState(false)

    useEffect(() => {
        if (sessionStorage.getItem("sessionExpired") === "1") {
            setSessionExpiredToastOpen(true)
            sessionStorage.removeItem("sessionExpired")
        }
    }, [])

    const from = location.state?.from?.pathname || "/home"

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setError(null)

        if (!email || !password) {
            setError(String(t("loginPage.errors.emailPasswordRequired")))
            return
        }

        try {
            const response = await login(email, password)
            const roles = Array.isArray(response?.user?.roles)
                ? response.user.roles
                : []
            const primaryRole = getPrimaryRole(roles)
            const recoverableSession =
                primaryRole === "cashier"
                    ? response?.cashierFlow?.recoverableSession
                    : null
            const requiresRegisterSelection =
                primaryRole === "cashier" &&
                response?.cashierFlow?.requiresRegisterSelection
            const defaultAfterLoginPath =
                primaryRole === "manager" ? "/home" : from

            if (
                recoverableSession?.companyId &&
                recoverableSession?.outletId &&
                recoverableSession?.registerId
            ) {
                navigate("/pos", {
                    replace: true,
                    state: {
                        selectedCompanyId: recoverableSession.companyId,
                        selectedOutletId: recoverableSession.outletId,
                        selectedRegisterId: recoverableSession.registerId,
                    },
                })
                return
            }

            if (requiresRegisterSelection) {
                navigate("/cash-register-selection", { replace: true })
            } else {
                navigate(defaultAfterLoginPath, { replace: true })
            }
        } catch (err: unknown) {
            setError(
                getErrorMessage(err, String(t("loginPage.errors.loginFailed")))
            )
        }
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #87171A 0%, #C6222B 100%)",
            }}
        >
            <Container maxWidth="xs">
                <Snackbar
                    open={sessionExpiredToastOpen}
                    autoHideDuration={5000}
                    onClose={() => setSessionExpiredToastOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert
                        onClose={() => setSessionExpiredToastOpen(false)}
                        severity="warning"
                        variant="filled"
                        sx={{
                            width: "100%",
                            alignItems: "center",
                            "& .MuiAlert-message": {
                                display: "flex",
                                alignItems: "center",
                                py: 0,
                            },
                            "& .MuiAlert-action": {
                                display: "flex",
                                alignItems: "center",
                                m: 0,
                                pl: 1.5,
                            },
                        }}
                    >
                        {t("loginPage.messages.sessionExpired")}
                    </Alert>
                </Snackbar>

                <Paper
                    elevation={6}
                    sx={{
                        padding: { xs: 4, sm: 5 },
                        borderRadius: 3,
                        width: "100%",
                    }}
                >
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                            }}
                        >
                            <img
                                src={miniCileamoov}
                                alt={String(t("loginPage.logoAlt"))}
                                style={{
                                    width: "90%",
                                    height: "90%",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{ fontWeight: 700, color: "#87171A" }}
                        >
                            {t("loginPage.title")}
                        </Typography>
                    </Box>

                    {(error || authError) && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error || authError}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label={String(t("loginPage.fields.email"))}
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#C6222B",
                                    },
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#C6222B",
                                },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label={String(t("loginPage.fields.password"))}
                            type={showPassword ? "text" : "password"}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                showPassword
                                                    ? "hide password"
                                                    : "show password"
                                            }
                                            onClick={() =>
                                                setShowPassword(
                                                    (prevShowPassword) =>
                                                        !prevShowPassword
                                                )
                                            }
                                            edge="end"
                                            disabled={loading}
                                        >
                                            {showPassword ? (
                                                <VisibilityOff />
                                            ) : (
                                                <Visibility />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#C6222B",
                                    },
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#C6222B",
                                },
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 3,
                                mb: 1,
                                py: 1.5,
                                borderRadius: 2,
                                backgroundColor: "#C6222B",
                                fontWeight: 600,
                                fontSize: "1rem",
                                "&:hover": { backgroundColor: "#87171A" },
                            }}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={24}
                                    sx={{ color: "#fff" }}
                                />
                            ) : (
                                t("loginPage.actions.login")
                            )}
                        </Button>
                    </Box>
                </Paper>

                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        textAlign: "center",
                        mt: 3,
                        color: "rgba(255,255,255,0.7)",
                    }}
                >
                    {t("loginPage.footer.poweredBy")}
                </Typography>
            </Container>
        </Box>
    )
}

export default LoginPage
